import React, { useState, useCallback, useEffect, useMemo } from 'react'
import SlideBase from '../SlideBase'
import WrappedImageBackground from '../../WrappedImageBackground'
import WorldMapAnimation from './WorldMapAnimation'
import QuizContent from './QuizContent'
import { buildHotelUrl } from 'home/components/TrendingDestinations/utils'
import FadeBackground from '../../FadeBackground'
import { useWrappedStorySelector, useWrappedStoryDispatch, WrappedStoryActions } from 'contexts/Wrapped'
import arrayShuffle from 'lib/array/arrayShuffle'

const SLIDE_ID = 'top-destinations-quiz' as const

function TopDestinationsQuizSlide() {
  // Use granular selectors to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const totalsData = useWrappedStorySelector(state => state.totalsData)
  const isPaused = useWrappedStorySelector(state => state.isPaused)
  const isActiveSlide = useWrappedStorySelector(state => state.currentSlide?.id === SLIDE_ID)
  const dispatch = useWrappedStoryDispatch()

  const [phase, setPhase] = useState<'question' | 'answer'>('question')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Totals-only mode is determined by not having user wrapped data
  const isTotalsOnlyMode = !data
  const { topDestinations: userTopDestinations, stats } = data || {}

  // Use totals data when user has no searched destinations or is in totals-only mode
  const useTotalsFallback = isTotalsOnlyMode || !userTopDestinations?.length
  const topDestinations = useMemo(
    () => useTotalsFallback ? (totalsData?.topDestinations ?? []) : (userTopDestinations ?? []),
    [useTotalsFallback, totalsData?.topDestinations, userTopDestinations],
  )
  const correctAnswer = topDestinations[0]

  // Shuffle destinations for display so the correct answer isn't always first
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const shuffledDestinations = useMemo(() => arrayShuffle(topDestinations), [])

  // Set context phase to 'question' when this slide becomes active to stop the timer
  useEffect(() => {
    if (isActiveSlide && phase === 'question') {
      dispatch({ type: WrappedStoryActions.setPhase, phase: 'question' })
    }
  }, [dispatch, isActiveSlide, phase])

  const handleSelect = useCallback((destinationId: string) => {
    if (phase === 'answer') {
      const url = buildHotelUrl(topDestinations?.find(topDestination => topDestination.id === destinationId)?.name ?? '', destinationId)
      window.open(url, '_blank')
    } else {
      setSelectedId(destinationId)
      setPhase('answer')
      // Reset context phase to undefined to resume the timer
      dispatch({ type: WrappedStoryActions.setPhase, phase: undefined })
    }
  }, [phase, topDestinations, dispatch])

  // Need destinations (from user or totals) to render
  if (!correctAnswer) return null

  if (phase === 'question') {
    return (
      <SlideBase verticalAlign="space-between">
        <WorldMapAnimation />
        <QuizContent
          topDestinations={shuffledDestinations}
          stats={stats}
          phase={phase}
          correctAnswerId={correctAnswer.id}
          selectedId={selectedId}
          onSelect={handleSelect}
          isTotalsMode={useTotalsFallback}
          totalsData={totalsData}
        />
      </SlideBase>
    )
  }

  return (
    <SlideBase
      verticalAlign={!correctAnswer?.imageId ? 'space-between' : 'end'}
      background={
        correctAnswer?.imageId && (
          <WrappedImageBackground
            gradientOverlay={false}
            imageId={correctAnswer.imageId}
            alt={correctAnswer.name}
            kenBurnsVariant="zoom"
            isActive={isActiveSlide && !isPaused}
          />
        )
      }
    >
      {!correctAnswer?.imageId && <WorldMapAnimation />}
      <FadeBackground variant="solid">
        <QuizContent
          topDestinations={shuffledDestinations}
          stats={stats}
          phase={phase}
          correctAnswerId={correctAnswer.id}
          selectedId={selectedId}
          onSelect={handleSelect}
          isTotalsMode={useTotalsFallback}
          totalsData={totalsData}
        />
      </FadeBackground>
    </SlideBase>
  )
}

export default React.memo(TopDestinationsQuizSlide)
